/**
 * Результат разговора: выбор метки, анкета заявки, время перезвона.
 *
 * Метка закрывает назначение и двигает круг на сервере (applyLabel, §6.4) —
 * здесь только сбор данных. Кнопки крупные и без подтверждения: подтверждение
 * на каждый разговор диспетчер начинает прокликивать не глядя, а двойной клик
 * сервер и так обрабатывает идемпотентно.
 *
 * Два «двухшаговых» результата (просьба Evgeniy 30.08.2026):
 * — «Заявка» открывает анкету по скрипту (имя, сумма, тип долга, город) и
 *   собирает стандартный текст лида сама — диспетчер ничего не сочиняет;
 * — метка с asksSchedule («Просил перезвонить») спрашивает «когда?» и шлёт
 *   время вместе с меткой — номер всплывёт у этого же диспетчера ровно тогда.
 *
 * С 29.08.2026 метка обязательна: пока по завершённому звонку не выбран
 * результат, набор следующего номера заблокирован (unlabeled.ts). Поэтому блок
 * умеет быть «требующим»: подсвечивается, когда без него работа стоит.
 */

import { useEffect, useRef, useState } from 'react';
import { PhoneForwarded } from 'lucide-react';
import type { DeskLabelBody, Label } from '@cc/shared';

import { Button } from '../../shared/ui/button.js';
import { formatDateTimeMsk, formatWhenMsk } from '../../shared/lib/msk.js';
import {
  CALLBACK_PRESETS,
  callbackInputBounds,
  callbackTimeIssue,
  parseMskDateTimeLocal,
} from './callback-time.js';
import {
  DEBT_TYPES,
  buildLeadComment,
  leadFormIssue,
  opensLeadForm,
  securedWarning,
  type DebtTypeKey,
  QUICK_FIELDS,
  type LeadFormValues,
} from './lead-form.js';
import {
  browserDraftStorage,
  clearLabelDraft,
  isEmptyLabelDraft,
  loadLabelDraft,
  restoredCallbackChoice,
  saveLabelDraft,
  type LabelDraft,
} from './label-draft.js';

/**
 * Подсказки под названием кнопки — по коду метки. Порог 300 000 ₽ назвал
 * Evgeniy 30.08.2026, он же зашит в анкету заявки (MIN_LEAD_DEBT_RUB).
 */
const LABEL_HINTS: Record<string, string> = {
  not_target: 'Разговор был, но долг меньше 300 000 ₽ или не подходит',
};

/**
 * Служебная метка протухания: её ставит система, кнопкой она не нужна и только
 * загромождает панель. Код продублирован с настройкой `expire_label_code` —
 * при смене кода в настройках поправить и здесь.
 */
const EXPIRE_LABEL_CODE = 'auto_expired';

/**
 * Пауза перед записью черновика (28.09.2026): пишем не на каждую букву, а когда
 * диспетчер на мгновение остановился. Перезагрузку внутри паузы ловит pagehide.
 */
const DRAFT_SAVE_DELAY_MS = 400;

type Mode =
  | { kind: 'pick' }
  | { kind: 'lead'; label: Label }
  | { kind: 'schedule'; label: Label };

const EMPTY_LEAD: LeadFormValues = {
  name: '',
  debtRub: '',
  debtTypes: [],
  worksWithFirm: null,
  income: null,
  overdue: null,
  bailiffs: null,
  property: null,
  city: '',
  note: '',
};

export interface LabelPickerProps {
  /** Справочник приходит отдельной ручкой GET /api/labels, в снапшоте пульта его нет */
  labels: Label[];
  /** Пока запрос идёт, все метки заблокированы: вторая метка отменить первую не может */
  busy: boolean;
  /** Тело запроса собирается здесь целиком: пустой комментарий не отправляется вовсе */
  onPick: (body: DeskLabelBody) => void;
  /**
   * Номер, который метка закроет.
   *
   * Подписан над кнопками не для красоты: метка ставится на карточку, открытую
   * на экране, и промах виден только по номеру. 28.08.2026 за смену так
   * разошлись 18 карточек — размечены без единого звонка, пока девять реальных
   * разговоров остались без результата.
   */
  phone: string;
  /**
   * Метка обязательна прямо сейчас: звонок завершён, без результата набор
   * следующего номера не откроется. Блок подсвечивается и объясняет это словами.
   */
  demanding: boolean;
  /** Метки недоступны, пока идёт разговор: результат ставится после звонка */
  callInProgress: boolean;
  /** Перезвонить по этой же карточке без метки: разговор мог оборваться */
  onRedial?: () => void;
  /** Отказ сервера на прошлую метку; undefined — отказа не было */
  error?: string | undefined;
  /**
   * Чей это черновик (28.09.2026): диспетчер и назначение. Недописанная метка
   * хранится в браузере и возвращается после F5 и переключения карточек.
   * undefined — не хранить: в наблюдении за чужим пультом (см. desk.page).
   */
  draftOwner?: { userId: string; assignmentId: string } | undefined;
}

export function LabelPicker({
  labels,
  busy,
  onPick,
  phone,
  demanding,
  callInProgress,
  onRedial,
  error,
  draftOwner,
}: LabelPickerProps): React.JSX.Element {
  const draftUserId = draftOwner?.userId;
  const draftAssignmentId = draftOwner?.assignmentId;
  // Черновик читается один раз, при появлении карточки: панель пересоздаётся
  // по key={id}, так что «при появлении» — это и F5, и переключение карточек
  const [restored] = useState<LabelDraft | null>(() =>
    draftUserId === undefined || draftAssignmentId === undefined
      ? null
      : loadLabelDraft(browserDraftStorage(), draftUserId, draftAssignmentId, Date.now()),
  );
  const [mode, setMode] = useState<Mode>({ kind: 'pick' });
  const [comment, setComment] = useState(restored?.comment ?? '');
  const [needComment, setNeedComment] = useState<Label | null>(null);
  const [lead, setLead] = useState<LeadFormValues>(
    restored === null ? EMPTY_LEAD : { ...EMPTY_LEAD, ...restored.lead },
  );
  const [leadTouched, setLeadTouched] = useState(false);
  // Пресет из черновика возвращается своим временем — см. restoredCallbackChoice
  const [restoredTime] = useState(() => (restored === null ? null : restoredCallbackChoice(restored)));
  /** null — время ещё не выбрано; строка 'custom' — своё время полем ниже */
  const [presetKey, setPresetKey] = useState<string | null>(restoredTime?.presetKey ?? null);
  const [customTime, setCustomTime] = useState(restoredTime?.customTime ?? '');
  /** Момент, который пресет дал при клике (ISO): он и уходит в черновик */
  const [presetAt, setPresetAt] = useState<string | null>(null);
  const [timeIssue, setTimeIssue] = useState<string | null>(null);
  /**
   * Метка, чья форма (заявка, перезвон) была открыта в черновике. Справочник
   * меток после F5 может прийти позже карточки — форма открывается, когда он
   * на месте. Пока ждём, черновик хранит именно её, а не «выбор метки».
   */
  const [pendingLabelId, setPendingLabelId] = useState<number | null>(
    restored !== null && restored.mode !== 'pick' ? restored.labelId : null,
  );

  useEffect(() => {
    if (pendingLabelId === null || labels.length === 0) return;
    const label = labels.find((l) => l.id === pendingLabelId);
    setPendingLabelId(null);
    // Метку убрали из справочника — остаётся выбор метки с тем, что введено
    if (label !== undefined && opensLeadForm(label)) setMode({ kind: 'lead', label });
    else if (label?.asksSchedule === true) setMode({ kind: 'schedule', label });
  }, [pendingLabelId, labels]);

  /**
   * Запись черновика с паузой. Пишется только изменившееся: иначе простой
   * просмотр карточки продлевал бы черновику срок жизни, а таймер, сработавший
   * после успешной метки, вернул бы уже удалённый черновик (см. send).
   */
  const draftMode: LabelDraft['mode'] =
    pendingLabelId !== null && restored !== null ? restored.mode : mode.kind;
  const draftLabelId = pendingLabelId ?? (mode.kind === 'pick' ? null : mode.label.id);
  const lastSavedRef = useRef<string | null>(null);
  const pendingSaveRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (draftUserId === undefined || draftAssignmentId === undefined) return;
    const body: Omit<LabelDraft, 'savedAt'> = {
      mode: draftMode,
      labelId: draftLabelId,
      comment,
      lead,
      presetKey,
      customTime,
      callbackAt: presetAt,
    };
    const json = JSON.stringify(body);
    // Первый проход — то, что уже лежит в хранилище (или пустая форма)
    if (lastSavedRef.current === null) {
      lastSavedRef.current = json;
      return;
    }
    const write = (): void => {
      pendingSaveRef.current = null;
      if (lastSavedRef.current === json) return;
      lastSavedRef.current = json;
      const storage = browserDraftStorage();
      if (isEmptyLabelDraft(body, EMPTY_LEAD)) clearLabelDraft(storage, draftUserId, draftAssignmentId);
      else saveLabelDraft(storage, draftUserId, draftAssignmentId, { ...body, savedAt: Date.now() });
    };
    pendingSaveRef.current = write;
    const id = setTimeout(write, DRAFT_SAVE_DELAY_MS);
    return () => {
      clearTimeout(id);
    };
  }, [draftUserId, draftAssignmentId, draftMode, draftLabelId, comment, lead, presetKey, customTime, presetAt]);

  // Недописанное внутри паузы не теряется: ни при F5 (pagehide), ни при
  // переключении карточки (размонтирование панели)
  useEffect(() => {
    const flush = (): void => {
      pendingSaveRef.current?.();
    };
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, []);

  /**
   * Отправка метки: сначала дописываем черновик. Отказ сервера — черновик
   * совпадает с экраном; успех — desk.page удаляет его, и запоздалый таймер
   * уже ничего не вернёт (записывать нечего: экран не менялся).
   */
  const send = (body: DeskLabelBody): void => {
    pendingSaveRef.current?.();
    onPick(body);
  };

  const disabled = busy || callInProgress;
  const visibleLabels = labels.filter((label) => label.code !== EXPIRE_LABEL_CODE);

  /**
   * После onPick состояние НЕ сбрасывается руками: при успехе карточка уходит
   * из resultTarget, и компонент пересоздаётся чистым через key={id} в
   * desk.page. При отказе сервера (обрыв сети, race_lost) всё введённое —
   * анкета, время, комментарий — остаётся на экране: диспетчер на линии не
   * должен переспрашивать клиента заново (ревью 30.08.2026).
   */
  const pick = (label: Label): void => {
    if (opensLeadForm(label)) {
      setMode({ kind: 'lead', label });
      return;
    }
    if (label.asksSchedule) {
      setMode({ kind: 'schedule', label });
      return;
    }
    const trimmed = comment.trim();
    if (label.requiresComment && trimmed === '') {
      setNeedComment(label);
      return;
    }
    setNeedComment(null);
    send({ labelId: label.id, ...(trimmed === '' ? {} : { comment: trimmed }) });
  };

  const submitLead = (label: Label): void => {
    setLeadTouched(true);
    if (leadFormIssue(lead) !== null) return;
    send({ labelId: label.id, comment: buildLeadComment(lead) });
  };

  /** Момент перезвона считается на отправке: «через час» — от клика «Поставить», а не от открытия */
  const resolveCallbackAt = (): Date | null => {
    if (presetKey === 'custom') return parseMskDateTimeLocal(customTime);
    const preset = CALLBACK_PRESETS.find((p) => p.key === presetKey);
    return preset === undefined ? null : preset.at(new Date());
  };

  const submitSchedule = (label: Label): void => {
    const at = resolveCallbackAt();
    const issue = callbackTimeIssue(at, new Date());
    setTimeIssue(issue);
    if (issue !== null || at === null) return;
    const trimmed = comment.trim();
    if (label.requiresComment && trimmed === '') {
      setNeedComment(label);
      return;
    }
    setNeedComment(null);
    send({
      labelId: label.id,
      nextActionAt: at.toISOString(),
      ...(trimmed === '' ? {} : { comment: trimmed }),
    });
  };

  // Компактно (просьба Evgeniy 04.09.2026: «метки на полэкрана»): две группы —
  // разговор был / не дозвонились, кнопки в одну строку высотой 40px, подсказки
  // в title, комментарий в две строки. Панель стала вдвое ниже.
  const talked = visibleLabels.filter((label) => label.isLead || label.asksSchedule || label.isNegative);
  const missed = visibleLabels.filter((label) => !(label.isLead || label.asksSchedule || label.isNegative));

  const labelButton = (label: Label): React.JSX.Element => (
    <button
      key={label.id}
      type="button"
      disabled={disabled}
      title={LABEL_HINTS[label.code]}
      onClick={() => {
        pick(label);
      }}
      className={
        'flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold ' +
        'select-none disabled:cursor-not-allowed disabled:opacity-50 ' +
        labelClasses(label)
      }
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: label.color === '' ? '#94a3b8' : label.color }}
        aria-hidden
      />
      <span className="truncate">{label.title}</span>
    </button>
  );

  return (
    <section
      className={
        'flex flex-col gap-3 rounded-2xl border bg-white p-4 ' +
        (demanding ? 'border-blue-300 ring-2 ring-blue-100' : 'border-slate-200')
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div className="flex min-w-0 flex-col">
          <h2 className="text-base font-bold text-slate-900">
            {mode.kind === 'lead'
              ? 'Заявка — анкета по скрипту'
              : mode.kind === 'schedule'
                ? 'Когда перезвонить?'
                : 'Результат разговора'}
          </h2>
          <p className="text-sm text-slate-500">
            <span className="font-semibold tabular-nums text-slate-900">{phone}</span>
            {mode.kind === 'pick' && demanding ? ' · без метки следующий номер не наберётся' : ''}
            {mode.kind === 'pick' && !demanding && callInProgress ? ' · метка после завершения звонка' : ''}
          </p>
        </div>
        {mode.kind === 'pick' && onRedial !== undefined ? (
          <Button variant="secondary" onClick={onRedial} disabled={busy || callInProgress}>
            <PhoneForwarded className="h-4 w-4" />
            Перезвонить
          </Button>
        ) : null}
      </div>

      {restored !== null ? (
        <p className="rounded-lg bg-amber-50 px-3 py-1.5 text-sm text-amber-800">
          Восстановлен черновик метки от {formatWhenMsk(new Date(restored.savedAt))} — проверьте и
          поставьте метку.
        </p>
      ) : null}

      {mode.kind === 'pick' ? (
        <>
          {talked.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Разговор был</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{talked.map(labelButton)}</div>
            </div>
          ) : null}
          {missed.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <p className="text-[11px] font-medium tracking-wide text-slate-400 uppercase">Не дозвонились</p>
              <div className="grid grid-cols-3 gap-2">{missed.map(labelButton)}</div>
            </div>
          ) : null}

          <textarea
            value={comment}
            onChange={(event) => {
              setComment(event.target.value);
            }}
            rows={2}
            disabled={callInProgress}
            aria-label="Комментарий"
            placeholder="Комментарий: что сказал клиент, о чём договорились"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline focus:outline-2 focus:outline-blue-500 disabled:bg-slate-50"
          />
        </>
      ) : null}

      {mode.kind === 'lead' ? (
        <LeadForm
          values={lead}
          onChange={setLead}
          touched={leadTouched}
          busy={busy}
          submitDisabled={callInProgress}
          onSubmit={() => {
            submitLead(mode.label);
          }}
          onBack={() => {
            setMode({ kind: 'pick' });
            setLeadTouched(false);
          }}
        />
      ) : null}

      {mode.kind === 'schedule' ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2.5">
            {[...CALLBACK_PRESETS.map((p) => ({ key: p.key, title: p.title })), { key: 'custom', title: 'Другое время' }].map(
              (option) => (
                <button
                  key={option.key}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setPresetKey(option.key);
                    setPresetAt(
                      CALLBACK_PRESETS.find((p) => p.key === option.key)?.at(new Date()).toISOString() ?? null,
                    );
                    setTimeIssue(null);
                  }}
                  className={
                    'h-11 rounded-xl border px-3 text-[15px] font-semibold select-none ' +
                    (presetKey === option.key
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')
                  }
                >
                  {option.title}
                </button>
              ),
            )}
          </div>

          {presetKey === 'custom' ? (
            <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
              Дата и время — московские
              <input
                type="datetime-local"
                value={customTime}
                onChange={(event) => {
                  setCustomTime(event.target.value);
                  setTimeIssue(null);
                }}
                {...callbackInputBounds(new Date())}
                className="rounded-xl border border-slate-300 px-3 py-2 text-base text-slate-900 focus:outline focus:outline-2 focus:outline-blue-500"
              />
            </label>
          ) : null}

          {/* Что реально уйдёт на сервер — и для пресета, и для своего времени:
              опечатку в годе видно здесь, до отправки */}
          {presetKey !== null ? <CallbackPreview at={resolveCallbackAt()} /> : null}

          <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
            О чём договорились — обязательно
            <textarea
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
              rows={2}
              placeholder="Например: обсудить списание, будет дома после 15:00"
              className="rounded-xl border border-slate-300 px-3 py-2 text-base text-slate-900 placeholder:text-slate-400 focus:outline focus:outline-2 focus:outline-blue-500"
            />
          </label>

          {timeIssue !== null ? (
            <p role="alert" className="text-sm text-red-700">
              {timeIssue}
            </p>
          ) : null}

          <div className="flex gap-2.5">
            {/* Заполнять форму во время разговора можно и нужно, отправлять —
                только после: метка закрывает карточку и снимает звонок с экрана */}
            <Button
              variant="primary"
              busy={busy}
              disabled={callInProgress}
              onClick={() => {
                submitSchedule(mode.label);
              }}
            >
              Поставить перезвон
            </Button>
            <Button
              onClick={() => {
                setMode({ kind: 'pick' });
                setTimeIssue(null);
              }}
            >
              Назад
            </Button>
          </div>
          <p className="text-sm text-slate-500">
            Номер вернётся именно вам в выбранное время. Ночное время сервер сдвинет
            на начало рабочего окна.
          </p>
        </div>
      ) : null}

      {needComment !== null ? (
        <p role="alert" className="text-sm text-red-700">
          Метка «{needComment.title}» требует комментария — напишите, что сказал клиент.
        </p>
      ) : null}

      {error !== undefined ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}

    </section>
  );
}

/** «Завтра в 10:00 → 31.08 10:00» — что реально уйдёт на сервер, до отправки */
function CallbackPreview({ at }: { at: Date | null }): React.JSX.Element | null {
  if (at === null) return null;
  return (
    <p className="text-sm text-slate-600">
      Перезвон:{' '}
      <span className="font-semibold text-slate-900">{formatDateTimeMsk(at)}</span> по Москве
    </p>
  );
}

function LeadForm({
  values,
  onChange,
  touched,
  busy,
  submitDisabled,
  onSubmit,
  onBack,
}: {
  values: LeadFormValues;
  onChange: (values: LeadFormValues) => void;
  touched: boolean;
  busy: boolean;
  /** Идёт разговор: заполнять можно, отправлять — после звонка (метка снимает карточку) */
  submitDisabled: boolean;
  onSubmit: () => void;
  onBack: () => void;
}): React.JSX.Element {
  const issue = leadFormIssue(values);
  const warning = securedWarning(values.debtTypes);

  const toggleDebt = (key: DebtTypeKey): void => {
    const has = values.debtTypes.includes(key);
    onChange({
      ...values,
      debtTypes: has ? values.debtTypes.filter((k) => k !== key) : [...values.debtTypes, key],
    });
  };

  const field =
    'rounded-xl border border-slate-300 px-3 py-2 text-base text-slate-900 ' +
    'placeholder:text-slate-400 focus:outline focus:outline-2 focus:outline-blue-500';

  return (
    <div className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
        Имя
        <input
          value={values.name}
          onChange={(event) => {
            onChange({ ...values, name: event.target.value });
          }}
          placeholder="Как к вам обращаться?"
          className={field}
        />
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
        Сумма долга, ₽
        <input
          value={values.debtRub}
          onChange={(event) => {
            onChange({ ...values, debtRub: event.target.value });
          }}
          inputMode="numeric"
          placeholder="450 000"
          className={field}
        />
      </label>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium text-slate-700">Какие долги назвал клиент</legend>
        <div className="flex flex-wrap gap-2">
          {DEBT_TYPES.map((type) => {
            const active = values.debtTypes.includes(type.key);
            return (
              <button
                key={type.key}
                type="button"
                onClick={() => {
                  toggleDebt(type.key);
                }}
                className={
                  'h-10 rounded-xl border px-3 text-sm font-semibold select-none ' +
                  (active
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')
                }
              >
                {type.title}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="text-sm font-medium text-slate-700">
          Уже работает с другой компанией по списанию?
        </legend>
        <div className="flex gap-2">
          {(
            [
              { key: 'yes', title: 'Да' },
              { key: 'no', title: 'Нет' },
            ] as const
          ).map((option) => {
            const active = values.worksWithFirm === option.key;
            return (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  onChange({ ...values, worksWithFirm: active ? null : option.key });
                }}
                className={
                  'h-10 rounded-xl border px-5 text-sm font-semibold select-none ' +
                  (active
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')
                }
              >
                {option.title}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Быстрые вопросы: один клик на ответ. Печатать в разговоре некогда,
          а покупателю лида эти факты нужны у каждой заявки */}
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-slate-700">Быстрые вопросы</legend>
        {QUICK_FIELDS.map((quick) => (
          <div key={quick.key} className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-slate-700">{quick.question}</span>
            <div className="flex gap-2">
              {(
                [
                  { key: 'yes', title: 'Да' },
                  { key: 'no', title: 'Нет' },
                ] as const
              ).map((option) => {
                const active = values[quick.key] === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      onChange({ ...values, [quick.key]: active ? null : option.key });
                    }}
                    className={
                      'h-9 rounded-lg border px-4 text-sm font-semibold select-none ' +
                      (active
                        ? 'border-blue-600 bg-blue-600 text-white'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
                    }
                  >
                    {option.title}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </fieldset>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
        Город
        <input
          value={values.city}
          onChange={(event) => {
            onChange({ ...values, city: event.target.value });
          }}
          placeholder="Вы из какого города?"
          className={field}
        />
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
        Что ещё сказал клиент — необязательно
        <textarea
          value={values.note}
          onChange={(event) => {
            onChange({ ...values, note: event.target.value });
          }}
          rows={2}
          placeholder="Удобное время, детали ситуации"
          className={field}
        />
      </label>

      {warning !== null ? (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">{warning}</p>
      ) : null}

      {touched && issue !== null ? (
        <p role="alert" className="text-sm text-red-700">
          {issue}
        </p>
      ) : null}

      <div className="flex gap-2.5">
        <Button variant="primary" busy={busy} disabled={submitDisabled} onClick={onSubmit}>
          Отправить заявку
        </Button>
        <Button onClick={onBack}>Назад</Button>
      </div>
      {submitDisabled ? (
        <p className="text-sm text-slate-500">
          Идёт разговор — заявка отправится после завершения звонка.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Вид кнопки по смыслу метки, а не по коду: флаги is_lead/is_negative — часть
 * контракта, и новая метка админа сама получит правильный вид.
 */
function labelClasses(label: Label): string {
  if (label.isLead) {
    return 'border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-500';
  }
  if (label.isNegative) {
    return 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100';
  }
  return 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50';
}
